<template>
  <AuthWrapper>
    <template #card>
      <!-- A request PAC cannot verify: shown, never redirected -->
      <div v-if="fatal" class="text-center">
        <h4 class="text-danger">Could not start sign-in</h4>
        <p class="mb-0">{{ fatal }}</p>
      </div>

      <div v-else-if="state !== 'choose'" class="text-center py-2">
        <b-spinner variant="primary" class="mb-1" />
        <p class="mb-0">{{ client && state === 'working' ? `Signing you in to ${client.name}…` : "Loading…" }}</p>
      </div>

      <template v-else>
        <oauth-client-header :client="client" title="Choose an account" />

        <b-alert variant="danger" :show="!!error" class="mb-2">
          <div class="alert-body font-small-2">{{ error }}</div>
        </b-alert>

        <div class="list-group mb-1">
          <button type="button" class="list-group-item list-group-item-action d-flex align-items-center" :disabled="!!error" @click="proceed">
            <b-avatar :src="userAvatar || null" :text="userInitials" size="40" variant="light-primary" class="mr-1" />
            <div class="text-left overflow-hidden">
              <div class="font-weight-bold text-truncate">{{ userName }}</div>
              <small class="text-muted text-truncate d-block">{{ userEmail }}</small>
            </div>
            <feather-icon icon="ChevronRightIcon" class="ml-auto" />
          </button>
          <button type="button" class="list-group-item list-group-item-action d-flex align-items-center" @click="useAnotherAccount">
            <b-avatar size="40" variant="light-secondary" class="mr-1">
              <feather-icon icon="UserPlusIcon" />
            </b-avatar>
            <span>Use another account</span>
          </button>
        </div>

        <div class="small text-muted mt-2">
          <p class="mb-50">To continue, PAC will share with {{ client.name }}:</p>
          <ul class="pl-2 mb-1">
            <li v-for="line in sharedData" :key="line">{{ line }}</li>
          </ul>
          <p class="mb-0">
            Your password is never shared.
            <template v-if="client.website">
              Before using {{ client.name }}, you can review it at
              <b-link :href="client.website" target="_blank" rel="noopener">{{ websiteHost }}</b-link>.
            </template>
          </p>
        </div>

        <div class="d-flex justify-content-end mt-2">
          <b-button variant="link" class="p-0" @click="cancel">Cancel</b-button>
        </div>
      </template>
    </template>
  </AuthWrapper>
</template>

<script>
import { get } from "lodash";
import { BAlert, BAvatar, BButton, BLink, BSpinner } from "bootstrap-vue";
import { isUserLoggedIn } from "@/auth/utils";
import { checkLoginTokenStatus } from "@/libs/acl/routeProtection";
import { MUTATE_LOGIN_STATUS } from "@/store/config/mutation-keys";
import { oauthErrorUrl, saveOauthReturn, sharedDataDescriptions } from "@/libs/oauth-return";
import AuthWrapper from "@core/components/auth-wrapper/AuthWrapper.vue";
import OauthClientHeader from "@core/components/shared/oauth/OauthClientHeader.vue";

// P2: the PAC side of OIDC/PKCE sign-in. Shows which application is asking and which PAC account
// will be used, like a Google account chooser, then asks the API for a one-time code and sends the
// browser back to the application.
//
// `prompt` (passed through by GET /oauth/authorize):
//   none            no screens: continue if signed in, otherwise return login_required to the app
//   login           always ask for the password, even if a PAC session exists
//   select_account,
//   consent, unset  show this account screen
export default {
  components: { AuthWrapper, BAlert, BAvatar, BButton, BLink, BSpinner, OauthClientHeader },
  data() {
    return { client: null, user: null, state: "loading", fatal: "", error: "" };
  },
  computed: {
    request() {
      return this.$route.query;
    },
    sharedData() {
      return sharedDataDescriptions();
    },
    websiteHost() {
      try {
        return new URL(this.client.website).host;
      } catch (e) {
        return this.client.website;
      }
    },
    userName() {
      const first = get(this.user, "member_profile.first_name", "");
      const last = get(this.user, "member_profile.last_name", "");
      return get(this.user, "full_name") || `${first} ${last}`.trim() || this.userEmail;
    },
    userEmail() {
      return get(this.user, "email", "");
    },
    userAvatar() {
      return get(this.user, "member_profile.avatar.path", "");
    },
    userInitials() {
      return this.userName.split(/\s+/).map((w) => w[0] || "").join("").slice(0, 2)
        .toUpperCase();
    },
  },
  async created() {
    const q = this.request;

    try {
      const { data } = await this.useJwt().mainService.oauth.client({ client_id: q.client_id, redirect_uri: q.redirect_uri });
      this.client = data;
    } catch (error) {
      this.fatal = get(error, "response.data.error_description") || "This sign-in link is not valid. Go back to the application and try again.";
      return;
    }

    try {
      this.user = await this.sessionUser();

      if (!this.user) {
        if (q.prompt === "none") return this.backToApp("login_required");
        return this.goToLogin();
      }
      if (q.prompt === "login") {
        await this.signOutHere();
        return this.goToLogin();
      }
      // `continue` marks the return from PAC's own login page: the person has just signed in
      if (q.prompt === "none" || q.continue === "1") return this.proceed();

      this.state = "choose";
    } catch (error) {
      this.fatal = "Sign-in could not be started. Go back to the application and try again.";
    }
    return undefined;
  },
  methods: {
    // The signed-in user, or null when there is no session or it has expired. Not named
    // `currentUser`: the global mixin has a computed of that name, which would shadow it.
    async sessionUser() {
      if (!isUserLoggedIn()) return null;
      try {
        const { authorized, user } = await checkLoginTokenStatus();
        return authorized ? user : null;
      } catch (e) {
        return null;
      }
    },
    goToLogin() {
      const { prompt, continue: _, ...rest } = this.request;
      saveOauthReturn(rest);
      this.$router.replace({ name: "auth-login" }).catch(() => {});
    },
    async signOutHere() {
      try {
        await this.useJwt().mainService.auth.logout();
      } catch (e) { /* the local session is cleared regardless */ }
      this.useJwt().mainService.auth.clearAccessToken();
      this.$store.commit(`auth/${MUTATE_LOGIN_STATUS}`, false);
    },
    async useAnotherAccount() {
      await this.signOutHere();
      this.goToLogin();
    },
    backToApp(error) {
      window.location.assign(oauthErrorUrl(this.request, error));
    },
    cancel() {
      this.backToApp("access_denied");
    },
    async proceed() {
      const q = this.request;
      this.state = "working";
      this.error = "";
      try {
        const { data } = await this.useJwt().mainService.oauth.issueCode({
          client_id: q.client_id,
          redirect_uri: q.redirect_uri,
          code_challenge: q.code_challenge,
          code_challenge_method: q.code_challenge_method,
          scope: q.scope,
          state: q.state,
          nonce: q.nonce,
        });
        window.location.assign(data.redirect_to);
      } catch (error) {
        const reason = get(error, "response.data.error");
        // a silent request the user could act on is handed back to the app, per OIDC
        if (q.prompt === "none" && reason) return this.backToApp(reason === "access_denied" ? "access_denied" : "interaction_required");
        this.error = reason === "access_denied"
          ? `This account does not have access to ${this.client.name}. Use another account, or ask a PAC administrator for access.`
          : get(error, "response.data.error_description") || "Sign-in could not be completed. Please try again.";
        this.state = "choose";
      }
      return undefined;
    },
  },
};
</script>
